import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Handshake } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import useAuthStore from '../lib/auth-store';
import { apiFetch } from '../components/merchant/attributionApi';

export default function AcceptAffiliateInvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { login, isAuthenticated, user } = useAuthStore();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');
  const [f, setF] = useState({ first_name: '', last_name: '', phone: '', password: '', confirm: '' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (isAuthenticated && user?.role === 'affiliate') return;
    apiFetch(null, `/affiliate-invite/${token}`).then(setInvite).catch((e) => setError(e.message));
  }, [token, isAuthenticated, user]);

  const submit = async (e) => {
    e.preventDefault();
    if (f.password !== f.confirm) { toast.error('Passwords do not match'); return; }
    setBusy(true);
    try {
      await apiFetch(null, `/affiliate-invite/${token}/accept`, 'POST', { first_name: f.first_name, last_name: f.last_name, phone: f.phone || undefined, password: f.password });
      await login(invite.email, f.password);
      toast.success('Welcome to your partner portal');
      navigate('/affiliate', { replace: true });
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  // login() briefly swaps the router for the global spinner; once signed in, go straight to the portal.
  if (isAuthenticated && user?.role === 'affiliate') return <Navigate to="/affiliate" replace />;

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4" data-testid="affiliate-accept-page">
      <Card className="w-full max-w-md p-8 space-y-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center"><Handshake className="w-5 h-5 text-primary" /></div>
          <div>
            <h1 className="text-xl font-bold">Partner portal invitation</h1>
            {invite && <p className="text-sm text-muted-foreground" data-testid="affiliate-accept-email">{invite.name} · {invite.email}</p>}
          </div>
        </div>
        {error ? <p className="text-sm text-red-600" data-testid="affiliate-accept-error">{error}</p> : (
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="First name" value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} required data-testid="affiliate-accept-first" />
              <Input placeholder="Last name" value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} required data-testid="affiliate-accept-last" />
            </div>
            <Input placeholder="Phone (optional)" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="affiliate-accept-phone" />
            <Input type="password" placeholder="Password (8+ chars, upper, lower, number)" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} required data-testid="affiliate-accept-password" />
            <Input type="password" placeholder="Confirm password" value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} required data-testid="affiliate-accept-confirm" />
            <Button type="submit" className="w-full" disabled={busy || !invite} data-testid="affiliate-accept-submit">{busy ? 'Creating account…' : 'Create my account'}</Button>
          </form>
        )}
      </Card>
    </div>
  );
}
